import { createFileRoute, Link, Navigate, redirect, useRouterState } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAbility } from '@features/authorization';
import { CrmUserProfilePaymentsPage } from '@pages/crm';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessCrmUserProfilePayments } from '@shared/lib';
import { Button } from '@shared/ui';

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/payments'
)({
  beforeLoad: ({ context, params }) => {
    const { ability, auth } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessCrmUserProfilePayments(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: CrmCustomerPaymentsRoute,
});

/** The Payment tab is a child of the existing User Profile record shell. */
function CrmCustomerPaymentsRoute() {
  const { t } = useTranslation();
  const ability = useAbility();
  const navigate = Route.useNavigate();
  const { userProfileId: customerId, locale } = Route.useParams();
  const search = Route.useSearch();
  const currentLocation = useRouterState({ select: (state) => state.location.href });
  const handleSearchQueryChange = useCallback(
    (paymentQ: string | undefined) => {
      void navigate({
        replace: true,
        resetScroll: false,
        search: (current) => ({ ...current, paymentQ }),
      });
    },
    [navigate]
  );

  // This is a presentation gate only. The shared Payment, Cart, and Cart Item
  // routes independently authorize all reads.
  if (!canAccessCrmUserProfilePayments(ability)) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  return (
    <CrmUserProfilePaymentsPage
      locale={locale}
      openInPaymentsControl={
        <Button asChild variant="outline">
          <Link
            to="/$locale/payments"
            params={{ locale }}
            search={{
              profileOriginCustomerId: customerId,
              profileOriginPaymentQ: search.paymentQ,
              profileOriginUserProfilePaymentsReturnTo: currentLocation,
              q: search.paymentQ,
              tab: 'invoices',
              userProfileId: customerId,
            }}
          >
            <ExternalLink aria-hidden="true" />
            {t('payments.openInPayments', 'Open in Payments')}
          </Link>
        </Button>
      }
      onOpenPayment={(paymentId, options) => {
        void navigate({
          to: '/$locale/payments/$paymentId',
          params: { locale, paymentId },
          search: {
            detailReturn: 'user-profile',
            detailReturnCustomerId: customerId,
            detailReturnQ: search.paymentQ,
            detailReturnUserProfilePaymentsReturnTo: currentLocation,
            detailTab: options?.initialTab,
          },
        });
      }}
      onSearchQueryChange={handleSearchQueryChange}
      searchQuery={search.paymentQ}
      userProfileId={customerId}
    />
  );
}
